// Port of webapp/src/components/viewHeader/filterComponent.tsx + filterEntry.tsx
// + filterValue.tsx, collapsed into one file because they are only used here.

import {ReactElement, useState} from 'react'
import {Board, IPropertyTemplate} from '../../types/board'
import {BoardView} from '../../types/boardView'
import {FilterClause, FilterCondition, FilterGroup} from '../../types/filterGroup'
import {IUser} from '../../types/user'
import * as mutator from '../../state/mutator'
import {Icon} from '../common/icons'
import {ButtonWithMenu, Menu, MenuItem, MenuSeparator} from '../common/menu'
import {parseArrayValue} from '../../state/properties'

type Props = {
    board: Board
    view: BoardView
    usersById: Record<string, IUser>
    readOnly: boolean
}

function filterableProperties(board: Board, view: BoardView): IPropertyTemplate[] {
    return board.cardProperties.filter((p) => p.type !== 'file' && !view.fields.filter.filters.some((f) => 'propertyId' in f && f.propertyId === p.id))
}

function conditionsFor(template: IPropertyTemplate): Array<{id: FilterCondition, label: string}> {
    switch (template.type) {
    case 'select':
    case 'multiSelect':
    case 'person':
    case 'multiPerson':
        return [
            {id: 'includes', label: '属于以下任一'},
            {id: 'notIncludes', label: '不属于以下任一'},
            {id: 'isEmpty', label: '为空'},
            {id: 'isNotEmpty', label: '不为空'},
        ]
    case 'date':
    case 'createdTime':
    case 'updatedTime':
        return [
            {id: 'is', label: '等于'},
            {id: 'isBefore', label: '早于'},
            {id: 'isAfter', label: '晚于'},
            {id: 'isEmpty', label: '为空'},
            {id: 'isNotEmpty', label: '不为空'},
        ]
    case 'checkbox':
        return [
            {id: 'isSet', label: '已勾选'},
            {id: 'isNotSet', label: '未勾选'},
        ]
    case 'number':
        return [
            {id: 'is', label: '等于'},
            {id: 'isEmpty', label: '为空'},
            {id: 'isNotEmpty', label: '不为空'},
        ]
    default:
        return [
            {id: 'contains', label: '包含'},
            {id: 'notContains', label: '不包含'},
            {id: 'isEmpty', label: '为空'},
            {id: 'isNotEmpty', label: '不为空'},
        ]
    }
}

function conditionLabel(clause: FilterClause, template: IPropertyTemplate): string {
    return conditionsFor(template).find((c) => c.id === clause.condition)?.label || clause.condition
}

function dayStartMs(dateString: string): number {
    const [y, m, d] = dateString.split('-').map(Number)
    const date = new Date(y, (m || 1) - 1, d || 1, 12)
    return date.getTime()
}

function isoDay(ms: number | undefined): string {
    if (!ms) {
        return new Date().toISOString().slice(0, 10)
    }
    const d = new Date(ms)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function FilterValueEditor({clause, template, usersById, onChange}: {
    clause: FilterClause
    template: IPropertyTemplate
    usersById: Record<string, IUser>
    onChange: (values: string[]) => void
}): ReactElement | null {
    const needsValue = !['isEmpty', 'isNotEmpty', 'isSet', 'isNotSet'].includes(clause.condition)
    if (!needsValue) {
        return null
    }

    if (['select', 'multiSelect'].includes(template.type)) {
        const selected = new Set(parseArrayValue(clause.values.length > 1 ? clause.values : clause.values[0]))
        return (
            <div className='max-h-52 overflow-auto py-1'>
                {template.options.length === 0 && <div className='px-3 py-1 text-xs opacity-60'>还没有选项</div>}
                {template.options.map((option) => (
                    <label key={option.id} className='Menu__item cursor-pointer'>
                        <input
                            type='checkbox'
                            className='mr-2'
                            checked={selected.has(option.id)}
                            onChange={() => {
                                const next = new Set(selected)
                                if (next.has(option.id)) {
                                    next.delete(option.id)
                                } else {
                                    next.add(option.id)
                                }
                                onChange([...next])
                            }}
                        />
                        {option.value}
                    </label>
                ))}
            </div>
        )
    }

    if (['person', 'multiPerson'].includes(template.type)) {
        const selected = new Set(clause.values)
        return (
            <div className='max-h-52 overflow-auto py-1'>
                {Object.values(usersById).map((user) => (
                    <label key={user.id} className='Menu__item cursor-pointer'>
                        <input
                            type='checkbox'
                            className='mr-2'
                            checked={selected.has(user.id)}
                            onChange={() => {
                                const next = new Set(selected)
                                if (next.has(user.id)) {
                                    next.delete(user.id)
                                } else {
                                    next.add(user.id)
                                }
                                onChange([...next])
                            }}
                        />
                        {user.username}
                    </label>
                ))}
            </div>
        )
    }

    if (['date', 'createdTime', 'updatedTime'].includes(template.type)) {
        return (
            <input
                type='date'
                className='m-2 rounded border border-main-fg/20 bg-transparent px-2 py-1 text-sm'
                value={isoDay(Number(clause.values[0]))}
                onChange={(event) => onChange([String(dayStartMs(event.target.value))])}
            />
        )
    }

    if (template.type === 'checkbox') {
        return (
            <div className='px-3 py-1 text-xs opacity-70'>
                {clause.condition === 'isSet' ? `仅显示「${template.name}」已勾选的卡片。` : `仅显示「${template.name}」未勾选的卡片。`}
            </div>
        )
    }

    return (
        <input
            type={template.type === 'number' ? 'number' : 'text'}
            className='m-2 w-44 rounded border border-main-fg/20 bg-transparent px-2 py-1 text-sm'
            placeholder={template.name}
            value={clause.values[0] || ''}
            onChange={(event) => onChange([event.target.value])}
        />
    )
}

function FilterEntry({board, view, clause, index, usersById, readOnly}: Props & {clause: FilterClause, index: number, usersById: Record<string, IUser>, readOnly: boolean}): ReactElement | null {
    const template = board.cardProperties.find((p) => p.id === clause.propertyId)
    if (!template) {
        return null
    }
    const [open, setOpen] = useState(false)

    const update = (patch: Partial<FilterClause>): void => {
        const filters = view.fields.filter.filters.map((f, i) => (i === index ? {...(f as FilterClause), ...patch} : f))
        void mutator.changeViewFilter(board.id, view, {...view.fields.filter, filters} as FilterGroup)
    }

    const chipText = [template.name, conditionLabel(clause, template)].join(' ')

    return (
        <div className='relative'>
            <button
                type='button'
                className='flex items-center gap-1 rounded-full bg-main-fg/8 px-2.5 py-1 text-xs hover:bg-main-fg/14'
                onClick={() => setOpen(!open)}
                disabled={readOnly}
            >
                {chipText}
                <Icon name='chevron-down' size={12}/>
            </button>
            {open && (
                <div className='Menu absolute left-0 top-full z-menu mt-1 w-auto p-0'>
                    <div className='py-1'>
                        <div className='px-3 py-1 text-xxs uppercase opacity-60'>{template.name}</div>
                        {conditionsFor(template).map((condition) => (
                            <MenuItem
                                key={condition.id}
                                checked={clause.condition === condition.id}
                                onClick={() => update({condition: condition.id, values: clause.condition === condition.id ? clause.values : []})}
                            >
                                {condition.label}
                            </MenuItem>
                        ))}
                        <MenuSeparator/>
                        <FilterValueEditor
                            clause={clause}
                            template={template}
                            usersById={usersById}
                            onChange={(values) => update({values})}
                        />
                        <MenuSeparator/>
                        <MenuItem
                            icon='trash'
                            danger={true}
                            onClick={() => void mutator.removeViewFilter(board.id, view, index)}
                        >
                            删除筛选条件
                        </MenuItem>
                    </div>
                </div>
            )}
        </div>
    )
}

export default function FilterComponent({board, view, usersById, readOnly}: Props): ReactElement {
    const clauses = view.fields.filter.filters.filter((f): f is FilterClause => 'condition' in f)
    const candidates = filterableProperties(board, view)

    const addFilter = (template: IPropertyTemplate): void => {
        const condition = conditionsFor(template)[0].id
        void mutator.addViewFilter(board.id, view, {propertyId: template.id, condition, values: []})
    }

    if (clauses.length === 0) {
        return (
            <ButtonWithMenu
                button={
                    <span className='flex cursor-pointer items-center gap-1 px-2 py-1 text-sm'>
                        <Icon name='filter' size={14}/>
                        筛选
                    </span>
                }
            >
                <Menu ariaLabel='添加筛选条件'>
                    {candidates.length === 0 && <MenuItem disabled={true}>所有属性都已设置筛选</MenuItem>}
                    {candidates.map((template) => (
                        <MenuItem key={template.id} onClick={() => addFilter(template)}>
                            {template.name}
                        </MenuItem>
                    ))}
                </Menu>
            </ButtonWithMenu>
        )
    }

    return (
        <div className='flex flex-wrap items-center gap-2'>
            <Icon name='filter' size={14} className='opacity-60'/>
            {clauses.map((clause, index) => (
                <FilterEntry
                    key={`${clause.propertyId}-${index}`}
                    board={board}
                    view={view}
                    clause={clause}
                    index={index}
                    usersById={usersById}
                    readOnly={readOnly}
                />
            ))}
            <ButtonWithMenu
                button={
                    <span className='flex cursor-pointer items-center gap-1 rounded-full px-2 py-1 text-xs hover:bg-main-fg/10'>
                        <Icon name='add' size={12}/>
                        添加
                    </span>
                }
            >
                <Menu ariaLabel='添加筛选条件'>
                    {candidates.map((template) => (
                        <MenuItem key={template.id} onClick={() => addFilter(template)}>
                            {template.name}
                        </MenuItem>
                    ))}
                </Menu>
            </ButtonWithMenu>
            <button
                type='button'
                className='text-xs opacity-60 hover:opacity-100'
                onClick={() => mutator.changeViewFilter(board.id, view, {operation: 'and', filters: []})}
            >
                清除全部
            </button>
        </div>
    )
}
